import {
    Component,
    OnInit,
    OnDestroy,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Input,
    ElementRef,
    Renderer2,
    AfterViewChecked
} from '@angular/core';
import { Subscription } from 'rxjs';
import { RealtimeSessionStreamService } from '../../../realtime-session-stream/realtime-session-stream.service';
import { ConversationMessage } from '../../../realtime-session-stream/conversation-message.interface';
import { CognitiveStateManagerService } from '../../../realtime-session-stream/cognitive-state-manager.service';
import { FeatureFlagService } from '../../../navigation/feature-flag.service';
import {
    StreamingEvent,
    SpanNode,
    CognitiveStreamState,
    ProcessedEvent
} from '../../../realtime-session-stream/streaming-event-schema.interface';
import { UtilsService } from '@craig/core';

/**
 * Component for displaying streaming messages with Chain of Thought visualization
 *
 * IMPORTANT ARCHITECTURAL DECISIONS:
 *
 * 1. DISPLAY-ONLY PERSISTENCE:
 * This component is DISPLAY-ONLY and does NOT persist any state to localStorage.
 * All persistence is handled by ConversationHistoryStorageService to maintain
 * a single source of truth and prevent localStorage quota issues.
 *
 * 2. FINAL RESPONSE SPAN SEPARATION:
 * Implements clean separation between cognitive processing and final response content:
 * - Cognitive events (craig.cognitive.*) → Chain of Thought section
 * - Final response tokens (response.llm.token inside FINAL_RESPONSE span) → Response section
 * - Ignores cognitive processing tokens (response.llm.token outside FINAL_RESPONSE span)
 *
 * The component:
 * - Displays cognitive state from CognitiveStateManager (for live streaming)
 * - Displays saved state from ConversationMessage (for historical messages)
 * - Tracks final response span markers to separate content streams
 * - Does NOT save anything to localStorage
 * - Does NOT call saveState() methods
 */
@Component({
    selector: 'craig-streaming-message',
    templateUrl: './streaming-message.component.html',
    styleUrls: ['./streaming-message.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class StreamingMessageComponent implements OnInit, OnDestroy, AfterViewChecked {
    // Inputs
    @Input() conversationMessage?: ConversationMessage;
    @Input() isHistorical: boolean = false;

    // Cognitive state - derived from conversation message (source of truth)
    cognitiveState: CognitiveStreamState | null = null;

    // Local preserved state to prevent disappearing when singleton state manager resets
    localCognitiveState: CognitiveStreamState | null = null;

    // UI state
    showChainOfThought = true;
    isChainOfThoughtEnabled = true; // CRITICAL: Feature flag for operational control of cognitive tree visibility
    isChainOfThoughtCollapsed = true; // Controls collapsed/expanded state - defaults to COLLAPSED for cleaner UX
    domainFilter: 'all' | 'signal' | 'insight' | 'executive' = 'all';
    showSlowOpsOnly = false;
    autoScroll = true;

    // Component state
    isStreaming = false;

    // Error state tracking
    noDocumentsRetrieved = false; // Tracks when no documents are available for response generation

    // Shimmer delay tracking - only show shimmer after 1 second to prevent flicker
    private activeEventStartTime: number = 0;
    private lastActiveEventId: string = '';
    private shimmerCheckInterval?: number;

    // Final response span tracking
    insideFinalResponse = false;
    finalResponseContent = '';

    // Separate content tracking
    cognitiveContent = ''; // For cognitive processing events

    // Timeout handling
    private streamTimeoutId?: any;
    private readonly STREAM_TIMEOUT_MS = 30000; // 30 seconds
    private lastEventTime: number = 0;

    // Subscriptions
    private subscriptions = new Subscription();
    private stateSubscription?: Subscription;
    private streamSubscription?: Subscription;
    private featureFlagSubscription?: Subscription;

    // Table column highlighting
    private tableHeaderListeners: Array<{ element: HTMLElement; listeners: (() => void)[] }> = [];
    private tablesInitialized = false;

    constructor(
        private changeDetectorRef: ChangeDetectorRef,
        private realtimeStreamService: RealtimeSessionStreamService,
        private cognitiveStateManager: CognitiveStateManagerService,
        private featureFlagService: FeatureFlagService,
        private elementRef: ElementRef,
        private renderer: Renderer2,
        private readonly utilsService: UtilsService
    ) {}

    // ========================================================================
    // Datasheet Display Helpers
    // ========================================================================

    get datasheetState(): any {
        return this.conversationMessage?.datasheetState;
    }

    get hasDatasheetSection(): boolean {
        const state = this.datasheetState;

        if (!state) {
            return false;
        }

        return (
            state.status === 'loading' ||
            state.status === 'found' ||
            state.status === 'none' ||
            state.status === 'unavailable'
        );
    }

    get datasheetLinks(): any[] {
        const items = this.datasheetState?.items || [];
        const seen = new Set<string>();
        return items.filter((item: any) => {
            const url = item.safeUrl;
            if (seen.has(url)) {
                return false;
            }
            seen.add(url);
            return true;
        });
    }

    /**
     * Resolve datasheet URL for the current environment and append hKey from localStorage
     */
    getDatasheetUrl(url: string): string {
        return this.utilsService.resolveDatasheetUrl(url);
    }

    /**
     * Track datasheet link clicks in New Relic and validate URL accessibility in background
     */
    onDatasheetLinkClick(sheet: any, index: number): void {
        const resolvedUrl = this.getDatasheetUrl(sheet.safeUrl);

        // Track the click immediately in New Relic
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const newrelic = (window as any).newrelic;

        if (newrelic && newrelic.addPageAction) {
            newrelic.addPageAction('datasheet_link_clicked', {
                timestamp: new Date().toISOString(),
                manufacturerName: sheet.manufacturerName,
                hasTitle: !!sheet.title,
                linkIndex: index,
                totalLinks: this.datasheetLinks.length,
                urlPath: this.extractUrlPath(resolvedUrl),
                hasHkey: resolvedUrl?.includes('hkey=') || false,
                messageId: this.conversationMessage?.messageId,
                featureUsed: 'datasheet_link_interaction'
            });
        }

        // Validate URL accessibility in background (non-blocking)
        // This runs in parallel with the link opening, tracks errors retroactively
        this.validateDatasheetUrl(sheet, resolvedUrl);

        // Note: Link opens normally via href attribute, validation happens in parallel
    }

    /**
     * Validate datasheet URL accessibility in background without blocking user
     */
    private async validateDatasheetUrl(sheet: any, url: string): Promise<void> {
        try {
            const response = await fetch(url, {
                method: 'HEAD',
                credentials: 'include',
                mode: 'cors'
            });

            if (response.status === 403) {
                this.trackDatasheetAccessError(sheet, url, '403', 'forbidden');
            } else if (!response.ok) {
                this.trackDatasheetAccessError(sheet, url, response.status.toString(), 'http_error');
            }
            // Success case - no need to track, link worked
        } catch (error) {
            // CORS or network error - track that validation couldn't be performed
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const newrelic = (window as any).newrelic;

            if (newrelic && newrelic.addPageAction) {
                newrelic.addPageAction('datasheet_validation_failed', {
                    timestamp: new Date().toISOString(),
                    manufacturerName: sheet.manufacturerName,
                    urlPath: this.extractUrlPath(url),
                    errorType: 'cors_or_network',
                    errorMessage: error instanceof Error ? error.message : 'Unknown error'
                });
            }
        }
    }

    /**
     * Track datasheet access errors in New Relic
     */
    private trackDatasheetAccessError(
        sheet: any,
        url: string,
        statusCode: string,
        errorCategory: string
    ): void {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const newrelic = (window as any).newrelic;

        if (newrelic && newrelic.addPageAction) {
            newrelic.addPageAction('datasheet_access_error', {
                timestamp: new Date().toISOString(),
                manufacturerName: sheet.manufacturerName,
                urlPath: this.extractUrlPath(url),
                statusCode: statusCode,
                errorCategory: errorCategory,
                messageId: this.conversationMessage?.messageId,
                featureUsed: 'datasheet_retrieval'
            });
        }
    }

    /**
     * Extract path from URL without query params for privacy
     */
    private extractUrlPath(url: string): string {
        try {
            const urlObj = new URL(url);
            return urlObj.pathname;
        } catch {
            return 'invalid_url';
        }
    }

    // ========================================================================
    // Lifecycle
    // ========================================================================

    ngOnInit(): void {
        // IMPORTANT: This component is display-only. All state persistence is handled
        // by ConversationHistoryStorageService to maintain a single source of truth.
        // This prevents localStorage quota issues and state conflicts.

        // Subscribe to feature flags
        this.featureFlagSubscription = this.featureFlagService.featureFlags$.subscribe((flags) => {
            // Use getFeatureFlag to respect local overrides
            this.isChainOfThoughtEnabled = this.featureFlagService.getFeatureFlag('isChainOfThoughtEnabled');
            this.changeDetectorRef.markForCheck();
        });

        // Only subscribe to cognitive state changes for live streaming
        // Historical messages should use their own saved state
        if (!this.isHistorical) {
            this.stateSubscription = this.cognitiveStateManager.state$.subscribe((state) => {
                this.cognitiveState = state;
                this.changeDetectorRef.markForCheck();
            });

            // CRITICAL: Subscribe to conversation state to trigger change detection for streaming content
            // This ensures the UI re-renders when message content updates
            const conversationSubscription = this.realtimeStreamService.conversation$.subscribe(() => {
                this.changeDetectorRef.markForCheck();
            });
            this.subscriptions.add(conversationSubscription);
        }

        // Determine mode - ONLY stream if explicitly marked as streaming
        const isActuallyStreaming =
            !this.isHistorical && this.conversationMessage?.streamingState?.isStreaming === true;

        if (isActuallyStreaming) {
            this.startStreaming();
        } else if (this.conversationMessage) {
            this.loadHistoricalMessage(this.conversationMessage);
        }

        // Always check for persisted noDocumentsRetrieved state on initialization

        if (
            this.conversationMessage?.streamingState &&
            (this.conversationMessage.streamingState as any).noDocumentsRetrieved
        ) {
            this.noDocumentsRetrieved = true;
        }
    }

    ngOnDestroy(): void {
        if (this.streamSubscription) {
            this.streamSubscription.unsubscribe();
        }
        if (this.stateSubscription) {
            this.stateSubscription.unsubscribe();
        }
        if (this.featureFlagSubscription) {
            this.featureFlagSubscription.unsubscribe();
        }
        this.subscriptions.unsubscribe();

        // Clean up shimmer check interval
        if (this.shimmerCheckInterval) {
            cancelAnimationFrame(this.shimmerCheckInterval);
            this.shimmerCheckInterval = undefined;
        }

        // Clean up table event listeners
        this.cleanupTableListeners();

        // NOTE: Removed saveState() call here to prevent localStorage quota errors
        // The conversation state is already being persisted by ConversationHistoryStorage service
        // when the stream completes, so saving it again in ngOnDestroy is redundant and
        // can cause QuotaExceededError when the cognitive state is large
    }

    ngAfterViewChecked(): void {
        // Initialize table column highlighting after content is rendered
        // Only run once when tables are first detected
        if (!this.tablesInitialized && this.displayContent) {
            this.initializeTableColumnHighlighting();
        }
    }

    // ========================================================================
    // Streaming Management
    // ========================================================================

    private startStreaming(): void {
        this.isStreaming = true;

        // Reset final response tracking
        this.insideFinalResponse = false;
        this.finalResponseContent = '';
        this.cognitiveContent = '';
        this.noDocumentsRetrieved = false; // Reset error state

        // Reset shimmer tracking for new stream
        this.lastActiveEventId = '';
        this.activeEventStartTime = 0;
        if (this.shimmerCheckInterval) {
            cancelAnimationFrame(this.shimmerCheckInterval);
            this.shimmerCheckInterval = undefined;
        }

        this.cognitiveStateManager.resetState();
        this.cognitiveStateManager.startStreaming();

        // CRITICAL: Unsubscribe any existing subscription first
        if (this.streamSubscription) {
            this.streamSubscription.unsubscribe();
        }

        // Subscribe to raw SSE events - ONLY ONE COMPONENT SHOULD DO THIS
        this.streamSubscription = this.realtimeStreamService.raw$.subscribe({
            next: (event: any) => {
                this.lastEventTime = Date.now();

                if (event.data === '[DONE]') {
                    this.handleStreamComplete(true);
                } else {
                    try {
                        const parsedEvent = this.parseSSEEvent(event);
                        if (parsedEvent) {
                            // Check for final response span markers first
                            if (this.isFinalResponseSpanMarker(parsedEvent)) {
                                this.handleFinalResponseSpanMarker(parsedEvent);
                                return; // Don't display the marker itself
                            }

                            // Check for error events
                            if (this.isErrorEvent(parsedEvent)) {
                                this.handleErrorEvent(parsedEvent);
                            } else {
                                // Route events based on type and span state
                                this.processStreamingEvent(parsedEvent);
                            }
                        }
                    } catch (error) {
                        console.error('Failed to parse SSE event:', error);
                    }
                }
            },
            error: (error) => {
                console.error('❌ Stream error:', error);
                this.handleStreamError(error);
            }
        });
    }

    private parseSSEEvent(event: any): StreamingEvent | null {
        try {
            // Handle raw SSE data
            if (typeof event.data === 'string') {
                const data = event.data.replace('data: ', '');
                if (data === '[DONE]') return null;

                // First try to parse as JSON - don't assume it's an error just because it contains 'error'
                try {
                    const parsed = JSON.parse(data);
                    // If it parses successfully, return it as-is
                    if (parsed.type && parsed.custom_outputs) {
                        return parsed as StreamingEvent;
                    }
                } catch (e) {
                    // Not JSON, continue with error string handling
                }

                // Check for error message format (including JSON errors) - only for non-JSON data
                if (data.includes('error') || data.includes('Error')) {
                    // Try to parse JSON error messages to extract the actual error
                    let errorMessage = data;
                    try {
                        const parsed = JSON.parse(data);
                        if (parsed.error) {
                            errorMessage = parsed.error;
                        } else if (parsed.message) {
                            errorMessage = parsed.message;
                        }
                    } catch (e) {
                        // Not JSON, use as is
                    }

                    return {
                        type: 'response.output_text.delta',
                        custom_outputs: {
                            event_type: 'response.cognitive.system.error',
                            event_category: 'cognitive',
                            delta: errorMessage
                        } as any,
                        delta: errorMessage,
                        item_id: '',
                        chunk_id: -1
                    } as StreamingEvent;
                }

                return JSON.parse(data);
            }

            // Handle pre-parsed events
            if (event.type && event.custom_outputs !== undefined) {
                return event as StreamingEvent;
            }

            return null;
        } catch (error) {
            console.error('Failed to parse event:', error, event);
            return null;
        }
    }

    private handleStreamComplete(success: boolean = true): void {
        this.isStreaming = false;

        // Clean up shimmer tracking
        if (this.shimmerCheckInterval) {
            cancelAnimationFrame(this.shimmerCheckInterval);
            this.shimmerCheckInterval = undefined;
        }
        this.lastActiveEventId = '';
        this.activeEventStartTime = 0;

        // REMOVED: updateMessageState() - now handled centrally in realtime-session-stream.service.ts
        // This prevents race conditions and ensures cognitive state is saved before streaming ends

        if (this.streamSubscription) {
            this.streamSubscription.unsubscribe();
            this.streamSubscription = undefined;
        }

        this.changeDetectorRef.markForCheck();
    }

    private isErrorEvent(event: StreamingEvent): boolean {
        const eventType = event.custom_outputs?.event_type;
        return (
            eventType === 'response.cognitive.system.error' ||
            eventType === 'response.cognitive.tool.error' ||
            eventType === 'craig.error.no_documents' ||
            eventType === 'SYSTEM_ERROR' ||
            eventType === 'TOOL_ERROR' ||
            (!!event.delta &&
                typeof event.delta === 'string' &&
                (event.delta.toLowerCase().includes('error') || event.delta.toLowerCase().includes('failed')))
        );
    }

    private isFinalResponseSpanMarker(event: StreamingEvent): boolean {
        const customOutputs = event.custom_outputs as any;
        return (
            (customOutputs?.response_span === 'START' && customOutputs?.span_type === 'FINAL_RESPONSE') ||
            (customOutputs?.response_span === 'END' && customOutputs?.span_type === 'FINAL_RESPONSE')
        );
    }

    private handleFinalResponseSpanMarker(event: StreamingEvent): void {
        const customOutputs = event.custom_outputs as any;

        if (customOutputs?.response_span === 'START' && customOutputs?.span_type === 'FINAL_RESPONSE') {
            this.insideFinalResponse = true;
            this.finalResponseContent = ''; // Reset final response content
        } else if (customOutputs?.response_span === 'END' && customOutputs?.span_type === 'FINAL_RESPONSE') {
            this.insideFinalResponse = false;
        }

        this.changeDetectorRef.markForCheck();
    }

    private processStreamingEvent(event: StreamingEvent): void {
        const customOutputs = event.custom_outputs as any;
        const eventType = customOutputs?.event_type;

        // SIMPLIFIED: Only handle final response tokens here
        // Cognitive processing is handled centrally in realtime-session-stream.service.ts
        if (eventType === 'response.llm.token' && this.insideFinalResponse) {
            // Add to final response content
            if (event.delta && typeof event.delta === 'string') {
                this.finalResponseContent += event.delta;
            }
        }
        // Accumulate cognitive content for display (but don't process)
        else if (event.custom_outputs?.event_category === 'cognitive' || this.isCognitiveEvent(eventType)) {
            if (event.delta && typeof event.delta === 'string') {
                this.cognitiveContent += event.delta;
            }
        }

        this.changeDetectorRef.markForCheck();
    }

    private isCognitiveEvent(eventType: string | undefined): boolean {
        if (!eventType) return false;

        const cognitiveEventTypes = [
            'craig.cognitive.node.start',
            'craig.cognitive.node.complete',
            'craig.cognitive.tool.start',
            'craig.cognitive.tool.update',
            'craig.cognitive.tool.complete',
            'craig.cognitive.signal.sanitization',
            'craig.cognitive.signal.classification',
            'craig.cognitive.signal.extraction',
            'craig.cognitive.processing',
            'craig.cognitive.classification.done'
        ];

        return cognitiveEventTypes.includes(eventType);
    }

    private handleErrorEvent(event: StreamingEvent): void {
        const eventType = event.custom_outputs?.event_type;

        console.log('🔍 🔥 Handling error event:', event);
        console.log('🔍 🔥 Event type:', eventType);
        const rawErrorMessage =
            (event.custom_outputs as any)?.delta || event.delta || 'An error occurred during processing';

        console.error('📛 Error event received:', rawErrorMessage, 'Event type:', eventType);

        // Handle specific error type for no documents retrieved
        if (eventType === 'craig.error.no_documents') {
            this.noDocumentsRetrieved = true;

            // Persist this state in the conversation message so it survives stream completion
            if (this.conversationMessage?.streamingState) {
                (this.conversationMessage.streamingState as any).noDocumentsRetrieved = true;
            } else {
            }

            this.changeDetectorRef.markForCheck();
            // Still process the event to show it in the cognitive tree (for debugging)
            this.cognitiveStateManager.processEvent(event);
            return;
        }

        // Convert technical error messages to user-friendly ones
        let friendlyMessage = 'Craig encountered an issue. Please try again.';

        if (typeof rawErrorMessage === 'string') {
            const errorLower = rawErrorMessage.toLowerCase();

            if (errorLower.includes('connection') || errorLower.includes('network')) {
                friendlyMessage = 'Craig is having trouble connecting. Please try again soon.';
            } else if (errorLower.includes('timeout')) {
                friendlyMessage = 'Craig is taking longer than expected. Please try again.';
            } else if (errorLower.includes('authentication') || errorLower.includes('unauthorized')) {
                friendlyMessage = 'Your session has expired. Please refresh the page and try again.';
            } else if (errorLower.includes('permission') || errorLower.includes('forbidden')) {
                friendlyMessage = 'Craig needs additional permissions to help with this request.';
            } else if (errorLower.includes('not found') || errorLower.includes('404')) {
                friendlyMessage = "Craig couldn't find what you're looking for. Please try rephrasing your request.";
            } else if (errorLower.includes('server') || errorLower.includes('internal')) {
                friendlyMessage = 'Craig is having trouble responding. Please try again soon.';
            } else if (errorLower.includes('local connection error')) {
                friendlyMessage = 'Craig is having trouble responding. Please try again soon.';
            }
        }

        this.changeDetectorRef.markForCheck();

        // Still process the event to show it in the cognitive tree (for debugging)
        this.cognitiveStateManager.processEvent(event);
    }

    private handleStreamError(error: any): void {
        console.error('❌ Stream connection error:', error);
        this.handleStreamComplete(false);
    }

    // ========================================================================
    // Historical Message Loading
    // ========================================================================

    private loadHistoricalMessage(message: ConversationMessage): void {
        // Load cognitive state from saved message
        if (message.cognitiveState) {
            // Set the cognitive state directly from the saved message
            this.cognitiveState = message.cognitiveState as any;

            // Restore UI state if available, otherwise default to COLLAPSED for cleaner UX
            if (message.cognitiveState.uiState) {
                this.isChainOfThoughtCollapsed = message.cognitiveState.uiState.isChainOfThoughtCollapsed;
            } else {
                this.isChainOfThoughtCollapsed = true; // DEFAULT TO COLLAPSED
            }
            this.localCognitiveState = message.cognitiveState as any;
        }

        // Restore noDocumentsRetrieved state from persisted streaming state

        if (message.streamingState && (message.streamingState as any).noDocumentsRetrieved) {
            this.noDocumentsRetrieved = true;
        }

        this.changeDetectorRef.markForCheck();
    }

    // ========================================================================
    // State Updates (No Persistence - that's handled by ConversationHistoryStorage)
    // ========================================================================

    // REMOVED: updateMessageState() - now handled centrally in realtime-session-stream.service.ts

    // ========================================================================
    // Template Helpers (for backward compatibility)
    // ========================================================================

    get cognitiveTree(): SpanNode[] {
        return this.filteredSpans;
    }

    get responseContent(): string {
        // CRITICAL: Always use the conversation message content
        // The service updates this correctly for both historical and streaming
        return this.conversationMessage?.content || '';
    }

    get cognitiveProcessingContent(): string {
        // Return cognitive processing content for display in cognitive section
        return this.cognitiveContent;
    }

    get finalResponseOnlyContent(): string {
        // Return only the final response content (tokens inside final response span)
        if (this.isStreaming) {
            return this.finalResponseContent;
        }
        // For historical messages, this would be the same as responseContent
        // unless we store them separately in the future
        return this.conversationMessage?.content || '';
    }

    get displayContent(): string {
        // IMPORTANT: This now returns ONLY the final response content
        // Cognitive processing content is shown in the Chain of Thought section
        const content = this.responseContent;

        // If no documents were retrieved and there's no response content, show fallback message
        if (this.noDocumentsRetrieved && (!content || content.trim().length === 0)) {
            const response_messages = [
                'No matching component found. Please confirm the part number or specifications.',
                'No results returned. Verify tolerances or broaden the search parameters.',
                'Component with the requested query could not be located. Adjust search criteria if needed.',
                'The requested part is not present in the database. Confirm designation or refine filters.',
                'No component identified. Review electrical characteristics or identifiers and try again.'
            ];

            // choose a random message from the array
            const randomMessage = response_messages[Math.floor(Math.random() * response_messages.length)];

            return randomMessage;
        }
        return content;
    }

    get statusMessage(): string {
        if (this.isStreaming && !this.responseContent) {
            return 'Craig is thinking...';
        }
        return '';
    }

    get responseLength(): number {
        return this.responseContent.length;
    }

    get totalEvents(): number {
        // 1:1 RELATIONSHIP: Live state during streaming, stored state after completion
        const currentState = this.cognitiveState || this.conversationMessage?.cognitiveState;
        return (currentState as any)?.totalEvents || 0;
    }

    get eventCount(): number {
        // Count the filtered events that are actually displayed to the user
        const eventGroups = this.chronologicalEventGroups;
        if (eventGroups.length === 0) {
            return 0;
        }

        // Count all events across all groups
        let totalFilteredEvents = 0;
        for (const group of eventGroups) {
            totalFilteredEvents += group.events.length;
        }

        return totalFilteredEvents;
    }

    get nodesExecuted(): number {
        // 1:1 RELATIONSHIP: Live state during streaming, stored state after completion
        const currentState = this.cognitiveState || this.conversationMessage?.cognitiveState;
        return (currentState as any)?.nodesExecuted || 0;
    }

    get toolsExecuted(): number {
        // 1:1 RELATIONSHIP: Live state during streaming, stored state after completion
        const currentState = this.cognitiveState || this.conversationMessage?.cognitiveState;
        return (currentState as any)?.toolsExecuted || 0;
    }

    get totalTime(): number {
        // 1:1 RELATIONSHIP: Live state during streaming, stored state after completion
        const currentState = this.cognitiveState || this.conversationMessage?.cognitiveState;
        return (currentState as any)?.totalDuration || 0;
    }

    get filteredSpans(): SpanNode[] {
        // 1:1 RELATIONSHIP: For historical messages, prioritize saved state. For streaming, use live state.
        let cognitiveState: any = null;

        if (this.isHistorical) {
            // Historical messages: prioritize saved state
            cognitiveState =
                this.conversationMessage?.cognitiveState || this.cognitiveState || this.localCognitiveState;
        } else {
            // Streaming messages: prioritize live state
            cognitiveState =
                this.cognitiveState || this.localCognitiveState || this.conversationMessage?.cognitiveState;
        }

        if (!cognitiveState?.rootSpans || cognitiveState.rootSpans.length === 0) {
            return [];
        }

        if (this.domainFilter === 'all') {
            return cognitiveState.rootSpans;
        }

        return this.filterSpansByDomain(cognitiveState.rootSpans, this.domainFilter);
    }

    /**
     * Get all events from all spans in CHRONOLOGICAL order (not hierarchical)
     * This provides the true Chain of Thought flow as events actually occurred
     */
    get chronologicalEvents(): ProcessedEvent[] {
        const allEvents: ProcessedEvent[] = [];

        // Collect all events from all spans recursively
        const collectEventsFromSpans = (spans: SpanNode[]) => {
            for (const span of spans) {
                // Add events from this span
                if (span.events && span.events.length > 0) {
                    // Filter out noise events just like we do for individual spans
                    const filteredEvents = span.events.filter((event) => !this.shouldFilterEvent(event));
                    allEvents.push(...filteredEvents);
                }

                // Recursively collect from child spans
                if (span.children && span.children.length > 0) {
                    collectEventsFromSpans(span.children);
                }
            }
        };

        // Get the current spans (respecting filters)
        const spans = this.filteredSpans;
        collectEventsFromSpans(spans);

        // Sort by timestamp first (primary), then by chunk_id (fallback)
        return allEvents.sort((a, b) => {
            // Primary sort: timestamp (when events were processed)
            if (a.timestamp !== b.timestamp) {
                return a.timestamp - b.timestamp;
            }
            // Fallback sort: chunk_id (order in original stream)
            return a.chunk_id - b.chunk_id;
        });
    }

    /**
     * Get all events from all spans in CHRONOLOGICAL order with span context for grouping
     * This provides the true Chain of Thought flow as events actually occurred, but allows UI grouping
     */
    get chronologicalEventsWithContext(): Array<{ event: ProcessedEvent; span: SpanNode }> {
        const allEventsWithContext: Array<{ event: ProcessedEvent; span: SpanNode }> = [];

        // Collect all events from all spans recursively with span context
        const collectEventsFromSpans = (spans: SpanNode[]) => {
            for (const span of spans) {
                // Add events from this span with span context
                if (span.events && span.events.length > 0) {
                    // Filter out noise events just like we do for individual spans
                    const filteredEvents = span.events.filter((event) => !this.shouldFilterEvent(event));
                    for (const event of filteredEvents) {
                        allEventsWithContext.push({ event, span });
                    }
                }

                // Recursively collect from child spans
                if (span.children && span.children.length > 0) {
                    collectEventsFromSpans(span.children);
                }
            }
        };

        // Get the current spans (respecting filters)
        const spans = this.filteredSpans;
        collectEventsFromSpans(spans);

        // Sort by timestamp first (primary), then by chunk_id (fallback)
        return allEventsWithContext.sort((a, b) => {
            // Primary sort: timestamp (when events were processed)
            if (a.event.timestamp !== b.event.timestamp) {
                return a.event.timestamp - b.event.timestamp;
            }
            // Fallback sort: chunk_id (order in original stream)
            return a.event.chunk_id - b.event.chunk_id;
        });
    }

    /**
     * Get chronologically ordered events grouped and consolidated into logical user-facing events
     * This maintains chronological order while consolidating related events (e.g., "Sanitizing: actual content")
     */
    get chronologicalEventGroups(): Array<{
        groupType: 'span_sequence' | 'mixed_sequence';
        span?: SpanNode;
        events: Array<{ event: ProcessedEvent; span: SpanNode; isConsolidated?: boolean }>;
        title?: string;
    }> {
        const eventsWithContext = this.chronologicalEventsWithContext;
        if (eventsWithContext.length === 0) {
            return [];
        }

        // First, consolidate related events within the chronological sequence
        const consolidatedEvents = this.consolidateChronologicalEvents(eventsWithContext);

        // Then group by spans while preserving chronological order
        const groups: Array<{
            groupType: 'span_sequence' | 'mixed_sequence';
            span?: SpanNode;
            events: Array<{ event: ProcessedEvent; span: SpanNode; isConsolidated?: boolean }>;
            title?: string;
        }> = [];

        let currentGroup: Array<{ event: ProcessedEvent; span: SpanNode; isConsolidated?: boolean }> = [];
        let currentSpan: SpanNode | null = null;

        for (const eventWithContext of consolidatedEvents) {
            const { event, span } = eventWithContext;

            // If this event is from the same span as the current group, add it
            if (currentSpan && currentSpan.span.span_id === span.span.span_id) {
                currentGroup.push(eventWithContext);
            } else {
                // Different span - finish current group if it exists
                if (currentGroup.length > 0 && currentSpan) {
                    groups.push({
                        groupType: 'span_sequence',
                        span: currentSpan,
                        events: currentGroup,
                        title: this.getSpanDisplayName(currentSpan)
                    });
                }

                // Start new group
                currentGroup = [eventWithContext];
                currentSpan = span;
            }
        }

        // Add the last group
        if (currentGroup.length > 0 && currentSpan) {
            groups.push({
                groupType: 'span_sequence',
                span: currentSpan,
                events: currentGroup,
                title: this.getSpanDisplayName(currentSpan)
            });
        }

        return groups;
    }

    /**
     * Consolidate related events in chronological order to create meaningful user-facing events
     * E.g., "Sanitizing user request" + actual content = "Sanitizing user request: what is the electronic component smbj15a"
     * Also filters out noise events and their associated content
     */
    private consolidateChronologicalEvents(
        eventsWithContext: Array<{ event: ProcessedEvent; span: SpanNode }>
    ): Array<{ event: ProcessedEvent; span: SpanNode; isConsolidated?: boolean }> {
        const consolidated: Array<{ event: ProcessedEvent; span: SpanNode; isConsolidated?: boolean }> = [];

        for (let i = 0; i < eventsWithContext.length; i++) {
            const current = eventsWithContext[i];
            const currentEvent = current.event;

            // Check if this event should be filtered out completely
            if (this.shouldFilterEvent(currentEvent)) {
                // If this is a consolidation candidate that we're filtering out,
                // we also need to skip its associated content events
                if (this.shouldConsolidateEvent(currentEvent)) {
                    i = this.skipConsolidatedEvents(eventsWithContext, i);
                }
                // Skip this filtered event (don't add to consolidated)
                continue;
            }

            // Check if this is a tool update event that should be consolidated with following content
            if (this.shouldConsolidateEvent(currentEvent)) {
                const delta = (currentEvent.delta || '').toLowerCase();

                // Check if this consolidation event should be completely filtered out
                const shouldFilterConsolidation = [
                    'sanitizing user request',
                    'acknowledging request',
                    'classifying request',
                    'extracting part numbers',
                    'determining active status'
                ].some((pattern) => delta.includes(pattern));

                if (shouldFilterConsolidation) {
                    // Special handling for noisy events that we want to completely filter out
                    if (delta.includes('acknowledging request')) {
                        // Create a clean "Processing request" event without the raw content
                        const cleanEvent: ProcessedEvent = {
                            ...currentEvent,
                            delta: 'Processing request',
                            event_type: 'cleaned_user_event'
                        };

                        consolidated.push({
                            event: cleanEvent,
                            span: current.span,
                            isConsolidated: true
                        });
                    }
                    // For all other noisy events (sanitizing, classifying, extracting, determining), completely filter them out

                    // Skip the content events that would have been consolidated (filter them out)
                    i = this.skipConsolidatedEvents(eventsWithContext, i);
                } else {
                    // Normal consolidation for other events
                    const consolidatedContent = this.findConsolidatedContent(eventsWithContext, i);

                    if (consolidatedContent) {
                        // Create consolidated event
                        const consolidatedEvent: ProcessedEvent = {
                            ...currentEvent,
                            delta: consolidatedContent,
                            event_type: 'consolidated_user_event'
                        };

                        consolidated.push({
                            event: consolidatedEvent,
                            span: current.span,
                            isConsolidated: true
                        });

                        // Skip the events we consolidated (they're already included)
                        i = this.skipConsolidatedEvents(eventsWithContext, i);
                    } else {
                        // No consolidation possible, add as-is
                        consolidated.push(current);
                    }
                }
            } else {
                // Not a consolidation candidate, add as-is (if not filtered)
                consolidated.push(current);
            }
        }

        return consolidated;
    }

    /**
     * Check if an event should be consolidated with following content
     */
    private shouldConsolidateEvent(event: ProcessedEvent): boolean {
        const eventType = event.event_type || '';
        const delta = event.delta?.toLowerCase() || '';

        // Tool update events that describe actions should be consolidated
        if (eventType === 'craig.cognitive.tool.update') {
            return (
                delta.includes('sanitizing') ||
                delta.includes('acknowledging') ||
                delta.includes('classifying') ||
                delta.includes('extracting') ||
                delta.includes('determining') ||
                delta.includes('preparing') ||
                delta.includes('generating')
            );
        }

        return false;
    }

    /**
     * Find content events that should be consolidated with a tool update event
     * Uses explicit span boundaries (start/complete events) instead of guessing
     */
    private findConsolidatedContent(
        eventsWithContext: Array<{ event: ProcessedEvent; span: SpanNode }>,
        startIndex: number
    ): string | null {
        const baseEvent = eventsWithContext[startIndex];
        const baseDelta = baseEvent.event.delta || '';
        const currentSpanId = baseEvent.span.span.span_id;

        // Determine what type of content we're looking for based on the action
        const expectedContentTypes = this.getExpectedContentTypes(baseEvent.event);
        if (expectedContentTypes.length === 0) {
            return null;
        }

        // Look for ALL events in this span until we hit the span's completion event
        const contentParts: string[] = [];

        for (let i = startIndex + 1; i < eventsWithContext.length; i++) {
            const candidate = eventsWithContext[i];

            // Stop if we hit a different span
            if (candidate.span.span.span_id !== currentSpanId) {
                break;
            }

            // Stop if we hit the span completion event (this marks the end of the span)
            const candidateType = candidate.event.event_type || '';
            if (this.isSpanCompletionEvent(candidateType)) {
                break;
            }

            // Collect ALL content events of the expected type within this span
            if (expectedContentTypes.includes(candidateType)) {
                const content = this.cleanDeltaText(candidate.event.delta || '').trim();
                if (content) {
                    contentParts.push(content);
                }
            }
        }

        if (contentParts.length > 0) {
            const consolidatedContent = contentParts.join(' ').trim();
            const cleanBaseDelta = this.cleanDeltaText(baseDelta);
            return `${cleanBaseDelta}: ${consolidatedContent}`;
        }

        return null;
    }

    /**
     * Check if an event type represents a span completion
     * Uses the exact CognitiveEventTypes constants from the schema
     */
    private isSpanCompletionEvent(eventType: string): boolean {
        return eventType === 'craig.cognitive.tool.complete' || eventType === 'craig.cognitive.node.complete';
    }

    /**
     * Get the expected content event types for a given base event
     */
    private getExpectedContentTypes(baseEvent: ProcessedEvent): string[] {
        const baseDelta = baseEvent.delta?.toLowerCase() || '';

        // Map tool update steps to their corresponding content event types
        if (baseDelta.includes('sanitizing')) {
            return ['craig.cognitive.signal.sanitization'];
        }
        if (baseDelta.includes('acknowledging')) {
            return ['craig.cognitive.signal.acknowledgment', 'craig.cognitive.signal.sanitization'];
        }
        if (baseDelta.includes('classifying')) {
            return ['craig.cognitive.signal.classification'];
        }
        if (baseDelta.includes('extracting')) {
            return ['craig.cognitive.signal.extraction'];
        }
        if (baseDelta.includes('determining')) {
            return ['craig.cognitive.signal.status', 'craig.cognitive.signal.determination'];
        }

        return [];
    }

    /**
     * Skip events that were consolidated into a previous event
     * This uses the same span boundary logic as findConsolidatedContent
     */
    private skipConsolidatedEvents(
        eventsWithContext: Array<{ event: ProcessedEvent; span: SpanNode }>,
        startIndex: number
    ): number {
        const baseEvent = eventsWithContext[startIndex];
        const currentSpanId = baseEvent.span.span.span_id;
        let skipIndex = startIndex;

        // Use the same logic as findConsolidatedContent to determine what to skip
        const expectedContentTypes = this.getExpectedContentTypes(baseEvent.event);
        if (expectedContentTypes.length === 0) {
            return skipIndex;
        }

        // Skip all events in this span until we hit the span completion event
        for (let i = startIndex + 1; i < eventsWithContext.length; i++) {
            const candidate = eventsWithContext[i];

            // Stop if we hit a different span
            if (candidate.span.span.span_id !== currentSpanId) {
                break;
            }

            // Stop if we hit the span completion event (this marks the end of the span)
            const candidateType = candidate.event.event_type || '';
            if (this.isSpanCompletionEvent(candidateType)) {
                break;
            }

            // If this is a content event of the expected type, mark it to be skipped
            if (expectedContentTypes.includes(candidateType)) {
                skipIndex = i;
            }
        }

        return skipIndex;
    }

    /**
     * Clean delta text by removing span prefixes and other formatting artifacts
     */
    private cleanDeltaText(text: string): string {
        if (!text) return '';

        // Remove span prefixes like [SIGNAL_COGNITIVE_TOOL], [DETAIL_NODE_COGNITIVE_NODE], etc.
        let cleaned = text.replace(/^\[[\w_]+\]\s*/, '');

        // Remove emoji prefixes
        cleaned = cleaned.replace(/^[🧠🔧⭐✨]+\s*/, '');

        // Clean up extra spaces
        cleaned = cleaned.replace(/\s+/g, ' ').trim();

        return cleaned;
    }

    get currentActiveStep(): SpanNode | null {
        // 1:1 RELATIONSHIP: For historical messages, prioritize saved state. For streaming, use live state.
        let cognitiveState: any = null;

        if (this.isHistorical) {
            // Historical messages: prioritize saved state
            cognitiveState =
                this.conversationMessage?.cognitiveState || this.cognitiveState || this.localCognitiveState;
        } else {
            // Streaming messages: prioritize live state
            cognitiveState =
                this.cognitiveState || this.localCognitiveState || this.conversationMessage?.cognitiveState;
        }
        if (!cognitiveState?.rootSpans) {
            return null;
        }

        // Find the currently active (running) span at ANY level in the hierarchy
        const findActiveSpan = (spans: SpanNode[]): SpanNode | null => {
            for (const span of spans) {
                if (span.isActive) {
                    return span;
                }
                // Recursively check children at all depths
                if (span.children && span.children.length > 0) {
                    const activeChild = findActiveSpan(span.children);
                    if (activeChild) {
                        return activeChild;
                    }
                }
            }
            return null;
        };

        const activeSpan = findActiveSpan(cognitiveState.rootSpans);
        if (activeSpan) {
            return activeSpan;
        }

        // If no active span found and we're streaming, return the most recent span with events
        if (this.isStreaming && cognitiveState.rootSpans.length > 0) {
            const findLastSpanWithActivity = (spans: SpanNode[]): SpanNode | null => {
                // Go through spans in reverse order to find the most recent activity
                for (let i = spans.length - 1; i >= 0; i--) {
                    const span = spans[i];

                    // Check children first (tools are more specific)
                    if (span.children && span.children.length > 0) {
                        const lastActiveChild = findLastSpanWithActivity(span.children);
                        if (lastActiveChild && lastActiveChild.events.length > 0) {
                            return lastActiveChild;
                        }
                    }

                    // Then check the span itself
                    if (span.events.length > 0) {
                        return span;
                    }
                }
                return null;
            };

            return findLastSpanWithActivity(cognitiveState.rootSpans);
        }

        return null;
    }

    get currentActiveEventSummary(): string {
        // Use the same filtered chronological events that are shown in the expanded view
        const eventGroups = this.chronologicalEventGroups;
        if (eventGroups.length === 0) {
            return '';
        }

        // Get the most recent filtered event from the chronological groups
        const lastGroup = eventGroups[eventGroups.length - 1];
        if (lastGroup.events.length === 0) {
            return '';
        }

        const lastEventWithContext = lastGroup.events[lastGroup.events.length - 1];
        const eventText = lastEventWithContext.event.delta || '';

        // Apply the same text filtering used in the expanded view
        return this.filterNodeText(eventText);
    }

    /**
     * Filter out all "_node" text from any display string
     * Made public so it can be used in templates
     */
    public filterNodeText(text: string): string {
        if (!text) return '';

        let filteredText = text;

        // If the text contains "_node", replace it with user-friendly alternatives
        if (filteredText.toLowerCase().includes('_node')) {
            filteredText =
                filteredText
                    .replace(/\b\w+_node\b/gi, 'Processing') // Replace any "word_node" with "Processing"
                    .replace(/_node/gi, '') // Remove any remaining "_node"
                    .replace(/\s+/g, ' ') // Clean up extra spaces
                    .trim() || 'Processing'; // Fallback if empty
        }

        return filteredText;
    }

    get hasChainOfThoughtContent(): boolean {
        // Always show chain of thought when feature is enabled AND we have content or are streaming
        // This ensures it remains available even after final response is complete
        const hasSpans = this.filteredSpans.length > 0;
        const hasCognitiveState = this.isHistorical && !!this.conversationMessage?.cognitiveState;

        return this.isChainOfThoughtEnabled && (hasSpans || this.isStreaming || hasCognitiveState);
    }

    private filterSpansByDomain(spans: SpanNode[], domain: string): SpanNode[] {
        return spans.filter((span) => {
            if (span.domain?.subdomain === domain) return true;
            if (span.children && span.children.length > 0) {
                return this.hasChildrenWithDomain(span.children, domain);
            }
            return false;
        });
    }

    private hasChildrenWithDomain(spans: SpanNode[], domain: string): boolean {
        return spans.some(
            (span) =>
                span.domain?.subdomain === domain ||
                (span.children && this.hasChildrenWithDomain(span.children, domain))
        );
    }

    toggleSpan(span: SpanNode): void {
        span.isExpanded = !span.isExpanded;
        this.changeDetectorRef.markForCheck();
    }

    toggleChainOfThought(): void {
        this.isChainOfThoughtCollapsed = !this.isChainOfThoughtCollapsed;
        this.changeDetectorRef.markForCheck();

        // Save UI state to cognitive state for persistence
        this.saveUIState();
    }

    /**
     * Save current UI state to cognitive state for persistence
     */
    private saveUIState(): void {
        if (this.cognitiveState) {
            // Ensure uiState exists
            if (!this.cognitiveState.uiState) {
                this.cognitiveState.uiState = { isChainOfThoughtCollapsed: false };
            }

            // Update UI state
            this.cognitiveState.uiState.isChainOfThoughtCollapsed = this.isChainOfThoughtCollapsed;

            // Also update the state manager if we're dealing with live streaming
            if (this.isStreaming && this.cognitiveStateManager) {
                this.cognitiveStateManager.updateUIState({
                    isChainOfThoughtCollapsed: this.isChainOfThoughtCollapsed
                });
            }
        }
    }

    getSpanIcon(span: SpanNode): string {
        const kind = span.span.span_kind;
        if (kind === 'cognitive_node') return 'Node';
        if (kind === 'cognitive_tool') return 'Tool';
        return 'Event';
    }

    getSpanDisplayName(span: SpanNode): string {
        const spanName = span.span.span_name;

        // Clean up span name but DON'T hide the entire span
        let displayName = spanName
            .replace(/_/g, ' ')
            .replace(/COGNITIVE NODE/g, '')
            .replace(/COGNITIVE TOOL/g, '')
            .trim();

        // Use the same comprehensive filtering as currentActiveEventSummary
        return this.filterNodeText(displayName) || 'Processing';
    }

    /**
     * Check if this event is the currently active/streaming event
     * Used to show shimmer effect on the active step like ChatGPT
     */
    isActiveEvent(event: ProcessedEvent, span: SpanNode | null): boolean {
        // Only show shimmer during active streaming
        if (!this.isStreaming) {
            return false;
        }

        // For chronological view (span is null), check if this is the last event overall
        if (span === null) {
            // Get the last event from chronological groups
            const groups = this.chronologicalEventGroups;
            if (groups.length === 0) return false;

            const lastGroup = groups[groups.length - 1];
            if (lastGroup.events.length === 0) return false;

            const lastEventWithContext = lastGroup.events[lastGroup.events.length - 1];
            return lastEventWithContext.event === event;
        }

        // For span-based view, check within the specific span
        try {
            let events: ProcessedEvent[];
            if (span.span?.span_kind === 'cognitive_tool') {
                events = this.getConsolidatedEvents(span);
            } else {
                events = this.getFilteredEvents(span);
            }

            // Show shimmer on the last event of this span
            return events.length > 0 && events[events.length - 1] === event;
        } catch (error) {
            // Fail gracefully if there are any issues
            return false;
        }
    }

    /**
     * Check if shimmer effect should be shown for this event
     * Only shows shimmer after event has been active for 1+ seconds to prevent flicker
     */
    shouldShowShimmer(event: ProcessedEvent, span: SpanNode | null): boolean {
        // First check if this is the active event
        const isActive = this.isActiveEvent(event, span);

        if (!isActive) {
            return false;
        }

        // Create a unique ID for this event
        const eventId = `${event.chunk_id}_${event.timestamp}`;

        // If this is a new active event, track its start time
        if (eventId !== this.lastActiveEventId) {
            this.lastActiveEventId = eventId;
            this.activeEventStartTime = Date.now();

            // Start checking for shimmer display using requestAnimationFrame
            this.startShimmerCheck();

            // Don't show shimmer yet (new event)
            return false;
        }

        // Check if 1 second has elapsed since this event became active
        const elapsedTime = Date.now() - this.activeEventStartTime;
        return elapsedTime >= 500; // Show shimmer after 1 second
    }

    /**
     * Start periodic shimmer check using requestAnimationFrame (no setTimeout)
     */
    private startShimmerCheck(): void {
        // Cancel any existing check
        if (this.shimmerCheckInterval) {
            cancelAnimationFrame(this.shimmerCheckInterval);
        }

        // Use requestAnimationFrame for smooth, GPU-friendly checks
        const checkShimmer = () => {
            if (this.isStreaming) {
                this.changeDetectorRef.markForCheck();
                this.shimmerCheckInterval = requestAnimationFrame(checkShimmer);
            }
        };

        this.shimmerCheckInterval = requestAnimationFrame(checkShimmer);
    }

    /**
     * Get consolidated content for tool spans - combines content between tool_update events
     */
    getConsolidatedToolContent(span: SpanNode): string {
        if (!span.events || span.events.length === 0) {
            return '';
        }

        const events = span.events;
        const consolidatedParts: string[] = [];
        let currentSegment: string[] = [];
        let toolUpdateContext = '';

        for (let i = 0; i < events.length; i++) {
            const event = events[i];
            const eventType = event.event_type || '';
            const delta = (event.delta || '').trim();

            if (eventType === 'craig.cognitive.tool.update') {
                // If we have accumulated content, consolidate it
                if (currentSegment.length > 0) {
                    const consolidated = currentSegment.join(' ').trim();
                    if (consolidated) {
                        if (toolUpdateContext) {
                            consolidatedParts.push(`${toolUpdateContext}: ${consolidated}`);
                        } else {
                            consolidatedParts.push(consolidated);
                        }
                    }
                    currentSegment = [];
                }

                // Set the context for the next segment
                toolUpdateContext = delta || 'Processing';
            } else if (delta && !delta.toLowerCase().includes('tool') && !delta.toLowerCase().includes('starting')) {
                // Accumulate content between tool_update events
                currentSegment.push(delta);
            }
        }

        // Handle any remaining content
        if (currentSegment.length > 0) {
            const consolidated = currentSegment.join(' ').trim();
            if (consolidated) {
                if (toolUpdateContext) {
                    consolidatedParts.push(`${toolUpdateContext}: ${consolidated}`);
                } else {
                    consolidatedParts.push(consolidated);
                }
            }
        }

        // Return the most recent consolidated part, or fall back to the tool name
        return consolidatedParts.length > 0
            ? consolidatedParts[consolidatedParts.length - 1]
            : toolUpdateContext || this.getSpanDisplayName(span);
    }

    /**
     * Get consolidated events for display in the UI - groups content between tool_update markers
     * and filters out noise events
     */
    getConsolidatedEvents(span: SpanNode): ProcessedEvent[] {
        if (!span.events || span.events.length === 0) {
            return [];
        }

        // First filter out noise events
        const filteredEvents = span.events.filter((event) => !this.shouldFilterEvent(event));

        if (filteredEvents.length === 0) {
            return [];
        }

        const consolidatedEvents: ProcessedEvent[] = [];
        let currentSegment: string[] = [];
        let toolUpdateContext = '';
        let toolUpdateEvent: ProcessedEvent | null = null;

        for (let i = 0; i < filteredEvents.length; i++) {
            const event = filteredEvents[i];
            const eventType = event.event_type || '';
            const delta = (event.delta || '').trim();

            if (eventType === 'craig.cognitive.tool.update') {
                // If we have a previous tool_update with accumulated content, create consolidated event
                if (toolUpdateEvent && currentSegment.length > 0) {
                    const consolidated = currentSegment.join(' ').trim();
                    if (consolidated) {
                        const consolidatedEvent: ProcessedEvent = {
                            ...toolUpdateEvent,
                            delta: `${toolUpdateContext}: ${consolidated}`,
                            event_type: 'consolidated_tool_content'
                        };
                        consolidatedEvents.push(consolidatedEvent);
                    }
                } else if (toolUpdateEvent && currentSegment.length === 0) {
                    // No content accumulated, just add the tool_update as-is
                    consolidatedEvents.push(toolUpdateEvent);
                }

                // Store current tool_update event and context for next consolidation
                toolUpdateContext = delta || 'Processing';
                toolUpdateEvent = event;
                currentSegment = [];
            } else if (delta && !delta.toLowerCase().includes('tool') && !delta.toLowerCase().includes('starting')) {
                // Accumulate content between tool_update events
                currentSegment.push(delta);
            } else if (eventType !== 'craig.cognitive.tool.update') {
                // Add other meaningful events as-is
                consolidatedEvents.push(event);
            }
        }

        // Handle the last tool_update event
        if (toolUpdateEvent) {
            if (currentSegment.length > 0) {
                const consolidated = currentSegment.join(' ').trim();
                if (consolidated) {
                    const consolidatedEvent: ProcessedEvent = {
                        ...toolUpdateEvent,
                        delta: `${toolUpdateContext}: ${consolidated}`,
                        event_type: 'consolidated_tool_content'
                    };
                    consolidatedEvents.push(consolidatedEvent);
                } else {
                    // No content to consolidate, add the tool_update as-is
                    consolidatedEvents.push(toolUpdateEvent);
                }
            } else {
                // No content accumulated, add the tool_update as-is
                consolidatedEvents.push(toolUpdateEvent);
            }
        }

        return consolidatedEvents;
    }

    /**
     * Get filtered events for regular (non-tool) spans
     */
    getFilteredEvents(span: SpanNode): ProcessedEvent[] {
        if (!span.events || span.events.length === 0) {
            return [];
        }

        return span.events.filter((event) => !this.shouldFilterEvent(event));
    }

    /**
     * Map event schema/type to appropriate icon name for streaming-icon component
     * Uses rich semantic mapping for visual variety and meaningful iconography
     */
    getEventIcon(event: ProcessedEvent): string {
        const eventType = event.event_type || '';
        const delta = event.delta?.toLowerCase() || '';

        // Priority check for generate/generating - always use sparkle for generative work
        if (delta.includes('generat') || delta.includes('creating') || delta.includes('crafting')) {
            return 'sparkle'; // Creative/generative work takes priority
        }

        // Primary mapping based on event schema patterns
        if (eventType.includes('REQUEST_ACKNOWLEDGMENT')) {
            return 'message'; // Communication/acknowledgment
        }
        if (eventType.includes('CLASSIFICATION') || eventType.includes('DECISION')) {
            return 'route'; // Decision making/routing
        }
        if (eventType.includes('SIGNAL_COMPLETE') || eventType.includes('COMPLETE')) {
            return 'sparkle'; // Completion/success
        }
        if (eventType.includes('TOOL_UPDATE') || eventType.includes('tool.')) {
            return 'tool'; // Tool operations
        }
        if (
            eventType.includes('DETAIL_ANALYSIS') ||
            eventType.includes('COMPARE_ANALYSIS') ||
            eventType.includes('SEARCH') ||
            eventType.includes('ANALYSIS')
        ) {
            return 'search'; // Analysis/searching
        }
        if (eventType.includes('consolidated_tool_content')) {
            return 'tool'; // Consolidated tool operations
        }

        // Enhanced semantic mapping based on content for visual variety
        if (delta.includes('sanitizing') || delta.includes('preparing') || delta.includes('context')) {
            return 'tool'; // Data preparation/cleaning
        }
        if (delta.includes('response')) {
            return 'sparkle'; // Response generation work
        }
        if (delta.includes('starting') || delta.includes('workflow') || delta.includes('routing')) {
            return 'route'; // Process initiation/routing
        }
        if (
            delta.includes('retrieving') ||
            delta.includes('searching') ||
            delta.includes('knowledge') ||
            delta.includes('documents') ||
            delta.includes('information')
        ) {
            return 'search'; // Information retrieval
        }
        if (
            delta.includes('classifying') ||
            delta.includes('determining') ||
            delta.includes('extracting') ||
            delta.includes('active status')
        ) {
            return 'route'; // Classification/analysis work
        }
        if (delta.includes('acknowledged') || delta.includes('request') || delta.includes('find')) {
            return 'message'; // Communication/requests
        }

        // Default thinking icon for general processing
        return 'thinking';
    }

    /**
     * Get the count of meaningful (filtered) events for display
     */
    getMeaningfulEventCount(span: SpanNode): number {
        if (span.span?.span_kind === 'cognitive_tool') {
            return this.getConsolidatedEvents(span).length;
        } else {
            return this.getFilteredEvents(span).length;
        }
    }

    /**
     * Check if an event should be filtered out of the UI (noise reduction)
     */
    private shouldFilterEvent(event: ProcessedEvent): boolean {
        const eventType = event.event_type || '';
        const delta = (event.delta || '').trim().toLowerCase();

        // Filter out administrative/lifecycle events that are just UI noise
        const filteredEventTypes = [
            'craig.cognitive.node.start',
            'craig.cognitive.node.complete',
            'craig.cognitive.tool.start',
            'craig.cognitive.tool.complete'
        ];

        // Also filter events with content that indicates start/complete operations
        const filteredContent = [
            'starting tool execution',
            'starting signal processing',
            'signal processing completed',
            'tool execution completed',
            'node started',
            'node completed',
            'starting node execution',
            'node execution completed'
        ];

        // Filter out specific noisy cognitive events as requested
        // Note: consolidation events like 'classifying request', 'extracting part numbers',
        // 'determining active status' are handled in consolidateChronologicalEvents()
        const noisyCognitiveEvents = [
            'classified as',
            'signal processing complete',
            'part analysis workflow',
            'prepared context',
            'generating comparison response',
            'generating detail response'
        ];

        // Check if event should be filtered based on delta content
        const shouldFilterByContent = noisyCognitiveEvents.some((noisePattern) => delta.includes(noisePattern));

        return (
            filteredEventTypes.includes(eventType) ||
            filteredContent.some((content) => delta.includes(content)) ||
            shouldFilterByContent
        );
    }

    onFilterChange(): void {
        this.changeDetectorRef.markForCheck();
    }

    // ========================================================================
    // Template tracking functions
    // ========================================================================

    trackBySpanId(index: number, span: SpanNode): string {
        return span?.span?.span_id || `fallback-${index}`;
    }

    trackByEventId(index: number, event: ProcessedEvent): string {
        return `${event.chunk_id}_${index}`;
    }

    trackByEventWithContext(
        index: number,
        eventWithContext: { event: ProcessedEvent; span: SpanNode; isConsolidated?: boolean }
    ): string {
        const consolidated = eventWithContext.isConsolidated ? '_consolidated' : '';
        return `${eventWithContext.event.chunk_id}_${eventWithContext.span.span.span_id}_${index}${consolidated}`;
    }

    trackByEventGroup(index: number, group: any): string {
        return `${group.groupType}_${group.span?.span?.span_id || index}_${group.events.length}`;
    }

    getExecutionTimeClass(time?: number): string {
        if (!time) return '';
        if (time > 5000) return 'time-critical';
        if (time > 1000) return 'time-slow';
        return 'time-normal';
    }

    // ========================================================================
    // Table Column Highlighting
    // ========================================================================

    /**
     * Initialize column highlighting for all tables in the component
     * This allows hovering over table headers to highlight the entire column
     */
    private initializeTableColumnHighlighting(): void {
        const tables = this.elementRef.nativeElement.querySelectorAll('table');

        if (tables.length === 0) {
            return;
        }

        this.tablesInitialized = true;

        tables.forEach((table: HTMLTableElement) => {
            const headers = table.querySelectorAll('thead th');

            headers.forEach((headerElement, columnIndex: number) => {
                const header = headerElement as HTMLElement;

                const mouseEnterListener = () => {
                    this.highlightColumn(table, columnIndex, true);
                };

                const mouseLeaveListener = () => {
                    this.highlightColumn(table, columnIndex, false);
                };

                // Add event listeners
                this.renderer.listen(header, 'mouseenter', mouseEnterListener);
                this.renderer.listen(header, 'mouseleave', mouseLeaveListener);

                // Store for cleanup
                this.tableHeaderListeners.push({
                    element: header,
                    listeners: [mouseEnterListener, mouseLeaveListener]
                });
            });
        });
    }

    /**
     * Highlight or unhighlight a column in a table
     */
    private highlightColumn(table: HTMLTableElement, columnIndex: number, highlight: boolean): void {
        // Highlight the header itself
        const header = table.querySelector(`thead th:nth-child(${columnIndex + 1})`) as HTMLElement;
        if (header) {
            if (highlight) {
                this.renderer.addClass(header, 'column-highlight');
            } else {
                this.renderer.removeClass(header, 'column-highlight');
            }
        }

        // Highlight all cells in this column
        const rows = table.querySelectorAll('tbody tr');
        rows.forEach((rowElement) => {
            const row = rowElement as HTMLElement;
            const cell = row.querySelector(`td:nth-child(${columnIndex + 1})`) as HTMLElement;
            if (cell) {
                if (highlight) {
                    this.renderer.addClass(cell, 'column-highlight');
                } else {
                    this.renderer.removeClass(cell, 'column-highlight');
                }
            }
        });
    }

    /**
     * Clean up table event listeners to prevent memory leaks
     */
    private cleanupTableListeners(): void {
        this.tableHeaderListeners = [];
        this.tablesInitialized = false;
    }
}
